import { fetchUtils } from 'react-admin';
import { API_URL, getApiEndpoint } from './config';

// Debug logging
console.log('🔧 Data Provider Config:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    apiUrl: API_URL,
    NODE_ENV: process.env.NODE_ENV
});

const httpClient = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }
    // Add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
        options.headers.set('Content-Type', 'application/json');
    }
    return fetchUtils.fetchJson(url, options);
};

// Helper function to ensure each item has an id field
const ensureIdField = (item) => {
    if (!item.id && item.ID) {
        return { ...item, id: item.ID };
    }
    return item;
};

// Helper function to handle paginated response
const handlePaginatedResponse = (json) => {
    // If the response is already in the correct format (has data and total)
    if (json.data && typeof json.total === 'number') {
        return {
            data: Array.isArray(json.data) ? json.data.map(ensureIdField) : [],
            total: json.total
        };
    }
    // If the response is just an array
    if (Array.isArray(json)) {
        return {
            data: json.map(ensureIdField),
            total: json.length
        };
    }
    // Fallback
    return { data: [], total: 0 };
};

export const dataProvider = {
    getList: async (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;

        const dedicatedAdminRoutes = ['orders', 'addresses', 'products', 'brands', 'blogs', 'imports'];
        
        // Build query string
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: perPage.toString(),
            sort_by: field,
            sort_order: order.toLowerCase(),
            ...params.filter
        }).toString();

        // Use admin endpoint for orders, addresses, products, brands, and blogs
        const url = dedicatedAdminRoutes.includes(resource) 
        ? `${API_URL}/${resource}/admin?${queryParams}`
        : `${API_URL}/${resource}?${queryParams}`;

        try {
            const { json } = await httpClient(url);
            return handlePaginatedResponse(json);
        } catch (error) {
            console.error('Data Provider Error:', error);
            return { data: [], total: 0 };
        }
    },

    getOne: async (resource, params) => {
        const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`);
        return { data: ensureIdField(json) };
    },

    getMany: async (resource, params) => {
        const queryParams = new URLSearchParams({
            ids: params.ids.join(','),
        }).toString();
        const url = `${API_URL}/${resource}?${queryParams}`;
        const { json } = await httpClient(url);
        return handlePaginatedResponse(json);
    },

    getManyReference: async (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: perPage.toString(),
            sort_by: field,
            sort_order: order.toLowerCase(),
            [params.target]: params.id,
            ...params.filter
        }).toString();

        const url = `${API_URL}/${resource}?${queryParams}`;
        const { json } = await httpClient(url);
        return handlePaginatedResponse(json);
    },

    create: async (resource, params) => {
        // Handle file uploads for products
        if (resource === 'products' && params.data.images) {
            const formData = new FormData();
            const data = { ...params.data };
            
            if (Array.isArray(params.data.images)) {
                params.data.images.forEach((file, index) => {
                    if (file.rawFile instanceof File) {
                        formData.append('images', file.rawFile);
                    }
                });
                delete data.images;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}`, {
                method: 'POST',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        // Handle file uploads for brands
        if (resource === 'brands' && (params.data.logo_image || params.data.banner_image)) {
            const formData = new FormData();
            const data = { ...params.data };
            
            // Handle logo image
            if (params.data.logo_image && params.data.logo_image.rawFile instanceof File) {
                formData.append('logo_image', params.data.logo_image.rawFile);
                delete data.logo_image;
            }
            
            // Handle banner image
            if (params.data.banner_image && params.data.banner_image.rawFile instanceof File) {
                formData.append('banner_image', params.data.banner_image.rawFile);
                delete data.banner_image;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}`, {
                method: 'POST',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        // Handle file uploads for blogs
        if (resource === 'blogs' && params.data.cover_image) {
            const formData = new FormData();
            const data = { ...params.data };
            
            // Handle cover image
            if (params.data.cover_image && params.data.cover_image.rawFile instanceof File) {
                formData.append('cover_image', params.data.cover_image.rawFile);
                delete data.cover_image;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}`, {
                method: 'POST',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        const { json } = await httpClient(`${API_URL}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        });
        return { data: ensureIdField(json) };
    },

    update: async (resource, params) => {
        // Handle file uploads for products
        if (resource === 'products' && params.data.images) {
            const formData = new FormData();
            const data = { ...params.data };
            
            if (Array.isArray(params.data.images)) {
                params.data.images.forEach((file, index) => {
                    if (file.rawFile instanceof File) {
                        formData.append('images', file.rawFile);
                    }
                });
                delete data.images;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`, {
                method: 'PUT',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        // Handle file uploads for brands
        if (resource === 'brands' && (params.data.logo_image || params.data.banner_image)) {
            const formData = new FormData();
            const data = { ...params.data };
            
            // Handle logo image
            if (params.data.logo_image && params.data.logo_image.rawFile instanceof File) {
                formData.append('logo_image', params.data.logo_image.rawFile);
                delete data.logo_image;
            }
            
            // Handle banner image
            if (params.data.banner_image && params.data.banner_image.rawFile instanceof File) {
                formData.append('banner_image', params.data.banner_image.rawFile);
                delete data.banner_image;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`, {
                method: 'PUT',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        // Handle file uploads for blogs
        if (resource === 'blogs' && params.data.cover_image) {
            const formData = new FormData();
            const data = { ...params.data };
            
            // Handle cover image
            if (params.data.cover_image && params.data.cover_image.rawFile instanceof File) {
                formData.append('cover_image', params.data.cover_image.rawFile);
                delete data.cover_image;
            }
            
            // Add other fields to formData
            Object.keys(data).forEach(key => {
                formData.append(key, data[key]);
            });

            const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`, {
                method: 'PUT',
                body: formData,
                headers: new Headers({
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                }),
            });
            return { data: ensureIdField(json) };
        }

        //handle dedicated admin route for orders
        if (resource === 'orders') {
            const { json } = await httpClient(`${API_URL}/${resource}/admin/${params.id}`, {
                method: 'PUT',
                body: JSON.stringify(params.data),
            });
            return { data: ensureIdField(json) };
        }

        const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        });
        return { data: ensureIdField(json) };
    },

    // Custom method for toggling product status
    toggleProductStatus: async (productId) => {
        const { json } = await httpClient(`${API_URL}/products/${productId}/toggle-active`, {
            method: 'PUT',
            body: JSON.stringify({}),
        });
        return { data: json };
    },

    // Custom methods for import functionality
    uploadImportFile: async (file, dryRun = false) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('dry_run', dryRun);

        const { json } = await httpClient(`${API_URL}/admin/import/products`, {
            method: 'POST',
            body: formData,
            headers: new Headers({
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            }),
        });
        return { data: json };
    },

    getImportStatus: async (jobId) => {
        const { json } = await httpClient(`${API_URL}/admin/import/products/status/${jobId}`);
        return { data: json };
    },

    downloadImportTemplate: async () => {
        const response = await fetch(`${API_URL}/admin/import/products/template`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        });
        return response.blob();
    },

    updateMany: async (resource, params) => {
        const responses = await Promise.all(
            params.ids.map(id =>
                httpClient(`${API_URL}/${resource}/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(params.data),
                })
            )
        );
        return { data: responses.map(({ json }) => ensureIdField(json)) };
    },

    delete: async (resource, params) => {
        const { json } = await httpClient(`${API_URL}/${resource}/${params.id}`, {
            method: 'DELETE',
        });
        return { data: ensureIdField(json) };
    },

    deleteMany: async (resource, params) => {
        const responses = await Promise.all(
            params.ids.map(id =>
                httpClient(`${API_URL}/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        );
        return { data: params.ids };
    },
}; 